import React, { useState, useEffect } from "react";
import { visuallyHidden } from "@mui/utils";
import { TextField, InputAdornment, Typography, Stack } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate, useParams } from "react-router-dom";
import AccessibleIconButton from "components/base/AccessibleIconButton";

const PulseSearchBar = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { category, breakcategory, search, index } = useParams();

  useEffect(() => {
    if (search) {
      setSearchTerm(decodeURIComponent(search));
    } else {
      setSearchTerm("");
    }
  }, [search]);

  const handleSearch = (event) => {
    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!searchTerm.trim()) return;

    const encodedSearch = encodeURIComponent(searchTerm.trim());
    let newPath = "/pulse";

    if (breakcategory) {
      newPath += `/breakout/${breakcategory}${
        index ? `/${index}` : ""
      }/search/${encodedSearch}`;
    } else if (category) {
      newPath += `/label/${category}/search/${encodedSearch}`;
    } else {
      newPath += `/search/${encodedSearch}`;
    }

    navigate(newPath);
    onSearch(searchTerm.trim());
  };

  const handleClear = (event) => {
    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }

    setSearchTerm("");
    let newPath = "/pulse";

    if (breakcategory) {
      newPath += `/breakout/${breakcategory}${index ? `/${index}` : ""}`;
    } else if (category) {
      newPath += `/label/${category}`;
    }

    navigate(newPath);
    onSearch("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSearch(e);
    }
  };

  return (
    <Stack mt={3} gap={1}>
      <TextField
        sx={{
          minWidth: { xs: "unset", md: "320px" },
          width: { md: "auto" },
          "& legend": {
            position: "absolute",
            backgroundColor: "transparent",
          },
        }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder="Search articles from last 30 days..."
        variant="outlined"
        size="small"
        label="Search Pulse"
        slotProps={{
          inputLabel: {
            sx: visuallyHidden,
          },
          input: {
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <AccessibleIconButton
                  title="Search Pulse"
                  onClick={handleSearch}
                  size="small"
                  type="button"
                  sx={{ p: 0, mr: 0, ml: 0 }}
                >
                  <SearchIcon />
                </AccessibleIconButton>
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end" sx={{ mr: 0 }}>
                <AccessibleIconButton
                  title="Clear Search"
                  onClick={handleClear}
                  size="small"
                  type="button"
                  sx={{ p: 0, mr: 0, ml: 0 }}
                >
                  <ClearIcon />
                </AccessibleIconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {breakcategory
          ? `Searching will show results from ${breakcategory}.`
          : category
          ? `Searching will show results from ${category}.`
          : "Searching will show results across all topics."}
      </Typography>
    </Stack>
  );
};

export default PulseSearchBar;
